import React, { useEffect, useRef, useState } from 'react';

/**
 * The Hedef Ata forms' konu picker: a <select> that can be searched.
 *
 * A ders carries hundreds of konular, so scrolling a native list to find one is
 * slow. While the list is on its way the field shows a spinner instead of an
 * empty, silently disabled box.
 */
interface Props {
  value: number | '';
  onChange: (v: number | '') => void;
  options: Array<{ id: number; title: string }>;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Border colour — the forms use blue for the lead field and grey for the rest. */
  borderColor?: string;
}

const F: React.CSSProperties = { fontFamily: 'Inter' };

// Hundreds of konular: open with the first rows, the rest is a click or a search away.
const OPTION_CAP = 60;

const KonuSelect: React.FC<Props> = ({
  value, onChange, options, loading = false, disabled = false, placeholder = 'Konu seçilmedi', borderColor = '#d8d8d8',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const blocked = disabled || loading;
  const current = options.find((o) => o.id === value);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) { setOpen(false); setQuery(''); setShowAll(false); }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const q = query.trim().toLocaleLowerCase('tr-TR');
  const shown = q ? options.filter((o) => o.title.toLocaleLowerCase('tr-TR').includes(q)) : options;
  const visible = showAll ? shown : shown.slice(0, OPTION_CAP);
  const hidden = shown.length - visible.length;
  const pick = (v: number | '') => { onChange(v); setOpen(false); setQuery(''); setShowAll(false); };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={blocked}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full bg-white rounded-[10px] px-3 py-2 text-left text-[13px] flex items-center justify-between gap-2 disabled:opacity-60"
        style={{ border: `1.5px solid ${borderColor}`, ...F }}
      >
        <span className={`truncate ${current && !loading ? 'text-[#1a1a1a]' : 'text-[#999]'}`}>
          {loading ? 'Konular yükleniyor…' : current?.title ?? placeholder}
        </span>
        {loading ? (
          <span className="w-4 h-4 rounded-full border-2 border-[#3973bf] border-t-transparent animate-spin flex-shrink-0" aria-hidden />
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="3" strokeLinecap="round" className="flex-shrink-0" aria-hidden><path d="M6 9l6 6 6-6" /></svg>
        )}
      </button>

      {open && !loading && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white rounded-[10px] shadow-[0px_4px_16px_rgba(0,0,0,0.15)] border border-[#e5e7eb] overflow-hidden">
          <input
            autoFocus
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowAll(false); }}
            onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } }}
            placeholder="Konu ara…"
            className="w-full px-3 py-2 text-[13px] outline-none border-b border-[#eef0f4] bg-[#f7f8fb]"
            style={F}
          />
          <ul role="listbox" className="max-h-[220px] overflow-y-auto">
            <li>
              <button type="button" onClick={() => pick('')} className="w-full text-left px-3 py-2 text-[13px] text-[#999] hover:bg-[#f3f4f6]" style={F}>
                {placeholder}
              </button>
            </li>
            {visible.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={o.id === value}
                  onClick={() => pick(o.id)}
                  className={`w-full text-left px-3 py-2 text-[13px] text-[#1a1a1a] hover:bg-[#eaf1fb] ${o.id === value ? 'bg-[#3973bf]/10' : ''}`}
                  style={F}
                >
                  {o.title}
                </button>
              </li>
            ))}
            {hidden > 0 && (
              <li>
                <button type="button" onClick={() => setShowAll(true)} className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#3973bf] hover:bg-[#eaf1fb]" style={F}>
                  {hidden} seçenek daha, tümünü göster
                </button>
              </li>
            )}
            {q && shown.length === 0 && (
              <li className="px-3 py-3 text-[13px] text-[#999]" style={F}>Eşleşen konu yok.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default KonuSelect;
